export const SCALAR_UNIT = "1";
/** The unit to print next to a value: its display symbol (µL for "uL"), or
 *  nothing for a dimensionless one. */
export function visibleUnit(unit) {
    return !unit || unit === SCALAR_UNIT ? "" : getDisplaySymbol(unit);
}
/**
 * Magic unit for an instant on the timeline. A value carrying this unit is
 * epoch seconds (UTC), NOT a dimensional quantity — it's deliberately absent
 * from the UNITS registry so dimensional conversion never touches it. The
 * formula engine treats it affinely (timestamp − timestamp → duration,
 * timestamp ± duration → timestamp) and renderers format it as a datetime.
 */
export const TIMESTAMP_UNIT_SECONDS = "timestamp_s_epoch";
export const UNITS = new Map([
    // Dimensionless units
    [
        "1",
        {
            symbol: "1",
            base_unit_symbol: "1",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Scalar",
            display_symbol: "",
            display_name: "No unit",
        },
    ],
    [
        "%",
        {
            symbol: "%",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Percent",
        },
    ],
    [
        "(dw)%",
        {
            symbol: "(dw)%",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Dry weight percent",
        },
    ],
    [
        "(w/w)%",
        {
            symbol: "(w/w)%",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Weight by weight percent",
        },
    ],
    [
        "(v/v)%",
        {
            symbol: "(v/v)%",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Volume by volume percent",
        },
    ],
    [
        "g/100 g",
        {
            symbol: "g/100 g",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Grams per hundred grams",
        },
    ],
    [
        "g/100g",
        {
            symbol: "g/100g",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_symbol: "g/100 g",
        },
    ],
    [
        "g/100g(%)",
        {
            symbol: "g/100g(%)",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_symbol: "g/100 g",
        },
    ],
    [
        "% (w/w)",
        {
            symbol: "% (w/w)",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_symbol: "%",
        },
    ],
    [
        "% (w/w) dm",
        {
            symbol: "% (w/w) dm",
            base_unit_symbol: "1",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Percent on dry matter",
        },
    ],
    [
        "RLU",
        {
            symbol: "RLU",
            base_unit_symbol: "1",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Scalar",
            display_name: "Relative Light Units",
        },
    ],
    // Mass fractions and liquid concentrations (lab report units)
    [
        "mg/kg",
        {
            symbol: "mg/kg",
            base_unit_symbol: "mg/kg",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Milligrams per kilogram",
        },
    ],
    [
        "mg/kg (ppm)",
        {
            symbol: "mg/kg (ppm)",
            base_unit_symbol: "mg/kg",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_symbol: "mg/kg",
        },
    ],
    [
        "µg/g",
        {
            symbol: "µg/g",
            base_unit_symbol: "mg/kg",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_symbol: "mg/kg",
        },
    ],
    [
        "ug/g",
        {
            symbol: "ug/g",
            base_unit_symbol: "mg/kg",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_symbol: "mg/kg",
        },
    ],
    [
        "ppm",
        {
            symbol: "ppm",
            base_unit_symbol: "mg/kg",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Parts per million",
        },
    ],
    [
        "g/kg",
        {
            symbol: "g/kg",
            base_unit_symbol: "mg/kg",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Grams per kilogram",
        },
    ],
    [
        "µg/kg",
        {
            symbol: "µg/kg",
            base_unit_symbol: "mg/kg",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Micrograms per kilogram",
        },
    ],
    [
        "ug/kg",
        {
            symbol: "ug/kg",
            base_unit_symbol: "mg/kg",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_symbol: "µg/kg",
        },
    ],
    [
        "ppb",
        {
            symbol: "ppb",
            base_unit_symbol: "mg/kg",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Parts per billion",
        },
    ],
    [
        "mg/100 g",
        {
            symbol: "mg/100 g",
            base_unit_symbol: "mg/kg",
            conversion_factor: 10,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Milligrams per hundred grams",
        },
    ],
    [
        "µg/100 g",
        {
            symbol: "µg/100 g",
            base_unit_symbol: "mg/kg",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_name: "Micrograms per hundred grams",
        },
    ],
    [
        "ug/100 g",
        {
            symbol: "ug/100 g",
            base_unit_symbol: "mg/kg",
            conversion_factor: 0.01,
            offset: 0,
            dimension_name: "Mass Fraction",
            display_symbol: "µg/100 g",
        },
    ],
    [
        "mg/L",
        {
            symbol: "mg/L",
            base_unit_symbol: "mg/L",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Concentration (liquid)",
            display_name: "Milligrams per litre",
        },
    ],
    [
        "mg/l",
        {
            symbol: "mg/l",
            base_unit_symbol: "mg/L",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Concentration (liquid)",
            display_symbol: "mg/L",
        },
    ],
    [
        "µg/L",
        {
            symbol: "µg/L",
            base_unit_symbol: "mg/L",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Mass Concentration (liquid)",
            display_name: "Micrograms per litre",
        },
    ],
    [
        "g/kg vet",
        {
            symbol: "g/kg vet",
            base_unit_symbol: "g/kg fat",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Fat Basis",
            display_symbol: "g/kg fat",
        },
    ],
    [
        "meq O/kg vet",
        {
            symbol: "meq O/kg vet",
            base_unit_symbol: "meq O/kg fat",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Peroxide Value",
            display_symbol: "meq O/kg fat",
        },
    ],
    [
        "g/kg fat",
        {
            symbol: "g/kg fat",
            base_unit_symbol: "g/kg fat",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Fat Basis",
            display_name: "Grams per kilogram fat",
        },
    ],
    [
        "meq O/kg fat",
        {
            symbol: "meq O/kg fat",
            base_unit_symbol: "meq O/kg fat",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Peroxide Value",
            display_name: "Milliequivalents oxygen per kilogram fat",
        },
    ],
    [
        "kJ/100 g",
        {
            symbol: "kJ/100 g",
            base_unit_symbol: "kJ/100 g",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Energy Density",
            display_name: "Kilojoules per hundred grams",
        },
    ],
    [
        "kcal/100 g",
        {
            symbol: "kcal/100 g",
            base_unit_symbol: "kJ/100 g",
            conversion_factor: 4.184,
            offset: 0,
            dimension_name: "Energy Density",
            display_name: "Kilocalories per hundred grams",
        },
    ],
    // Weight units
    [
        "g",
        {
            symbol: "g",
            base_unit_symbol: "g",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass",
        },
    ],
    [
        "kg",
        {
            symbol: "kg",
            base_unit_symbol: "g",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Mass",
        },
    ],
    [
        "mg",
        {
            symbol: "mg",
            base_unit_symbol: "g",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Mass",
        },
    ],
    [
        "ug",
        {
            symbol: "ug",
            base_unit_symbol: "g",
            conversion_factor: 0.000001,
            offset: 0,
            display_symbol: "µg",
            dimension_name: "Mass",
        },
    ],
    // Volume units
    [
        "L",
        {
            symbol: "L",
            base_unit_symbol: "L",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Volume",
        },
    ],
    [
        "mL",
        {
            symbol: "mL",
            base_unit_symbol: "L",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Volume",
        },
    ],
    [
        "uL",
        {
            symbol: "uL",
            base_unit_symbol: "L",
            conversion_factor: 0.000001,
            offset: 0,
            display_symbol: "µL",
            dimension_name: "Volume",
        },
    ],
    // Density units
    [
        "g/L",
        {
            symbol: "g/L",
            base_unit_symbol: "g/L",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Density",
        },
    ],
    [
        "kg/L",
        {
            symbol: "kg/L",
            base_unit_symbol: "g/L",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Density",
        },
    ],
    [
        "g/mL",
        {
            symbol: "g/mL",
            base_unit_symbol: "g/L",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Density",
        },
    ],
    [
        "kg/m3",
        {
            symbol: "kg/m3",
            base_unit_symbol: "g/L",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Density",
        },
    ],
    [
        "ug/mL",
        {
            symbol: "ug/mL",
            base_unit_symbol: "ug/mL",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Mass Concentration",
            display_symbol: "µg/mL",
        },
    ],
    // Molar units
    [
        "mol",
        {
            symbol: "mol",
            base_unit_symbol: "mol",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Amount",
        },
    ],
    [
        "mmol",
        {
            symbol: "mmol",
            base_unit_symbol: "mol",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Amount",
        },
    ],
    [
        "umol",
        {
            symbol: "umol",
            base_unit_symbol: "mol",
            conversion_factor: 0.000001,
            offset: 0,
            dimension_name: "Amount",
        },
    ],
    // Molecular weight
    [
        "g/mol",
        {
            symbol: "g/mol",
            base_unit_symbol: "g/mol",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Molecular Weight",
        },
    ],
    [
        "Da",
        {
            symbol: "Da",
            base_unit_symbol: "g/mol",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Molecular Weight",
        },
    ],
    [
        "kDa",
        {
            symbol: "kDa",
            base_unit_symbol: "g/mol",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Molecular Weight",
        },
    ],
    // Concentration units
    [
        "mol/L",
        {
            symbol: "mol/L",
            base_unit_symbol: "mol/L",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Concentration",
        },
    ],
    [
        "mmol/L",
        {
            symbol: "mmol/L",
            base_unit_symbol: "mol/L",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Concentration",
        },
    ],
    [
        "M",
        {
            symbol: "M",
            base_unit_symbol: "mol/L",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Concentration",
        },
    ],
    // pH unit
    [
        "pH",
        {
            symbol: "pH",
            base_unit_symbol: "pH",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "pH",
        },
    ],
    // RPM unit
    [
        "rpm",
        {
            symbol: "rpm",
            base_unit_symbol: "rpm",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "RPM",
        },
    ],
    // Temperature units
    [
        "C",
        {
            symbol: "C",
            base_unit_symbol: "K",
            conversion_factor: 1,
            offset: 273.15,
            display_symbol: "°C",
            dimension_name: "Temperature",
        },
    ],
    [
        "K",
        {
            symbol: "K",
            base_unit_symbol: "K",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Temperature",
        },
    ],
    // G-Force for centrifugation
    [
        "G",
        {
            symbol: "G",
            base_unit_symbol: "G",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "G-Force",
            display_symbol: "G",
            display_name: "G-Force",
        },
    ],
    // Time units
    [
        "s",
        {
            symbol: "s",
            base_unit_symbol: "s",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Time",
            display_name: "Second",
        },
    ],
    [
        "min",
        {
            symbol: "min",
            base_unit_symbol: "s",
            conversion_factor: 60,
            offset: 0,
            dimension_name: "Time",
            display_name: "Minute",
        },
    ],
    [
        "h",
        {
            symbol: "h",
            base_unit_symbol: "s",
            conversion_factor: 3600,
            offset: 0,
            dimension_name: "Time",
            display_name: "Hour",
        },
    ],
    [
        "d",
        {
            symbol: "d",
            base_unit_symbol: "s",
            conversion_factor: 86400,
            offset: 0,
            dimension_name: "Time",
            display_name: "Day",
        },
    ],
    // Length units
    [
        "m",
        {
            symbol: "m",
            base_unit_symbol: "m",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Length",
            display_name: "Meter",
        },
    ],
    // Flow Rate units
    [
        "L/h",
        {
            symbol: "L/h",
            base_unit_symbol: "L/h",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Flow Rate",
        },
    ],
    [
        "L/min",
        {
            symbol: "L/min",
            base_unit_symbol: "L/h",
            conversion_factor: 60,
            offset: 0,
            dimension_name: "Flow Rate",
            display_name: "Liters per minute",
        },
    ],
    [
        "mL/min",
        {
            symbol: "mL/min",
            base_unit_symbol: "L/h",
            conversion_factor: 0.06,
            offset: 0,
            dimension_name: "Flow Rate",
            display_name: "Milliliters per minute",
        },
    ],
    [
        "m3/h",
        {
            symbol: "m3/h",
            base_unit_symbol: "L/h",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Flow Rate",
            display_symbol: "m³/h",
            display_name: "Cubic meters per hour",
        },
    ],
    // Area units
    [
        "m2",
        {
            symbol: "m2",
            base_unit_symbol: "m2",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Area",
            display_symbol: "m²",
            display_name: "Square meter",
        },
    ],
    // Flux units
    [
        "LMH",
        {
            symbol: "LMH",
            base_unit_symbol: "LMH",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Flux",
            display_name: "Liters per square meter per hour",
        },
    ],
    // Pressure units
    [
        "bar",
        {
            symbol: "bar",
            base_unit_symbol: "bar",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Pressure",
            display_name: "Bar",
        },
    ],
    // Force units
    [
        "N",
        {
            symbol: "N",
            base_unit_symbol: "N",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Force",
            display_name: "Newton",
        },
    ],
    [
        "mN",
        {
            symbol: "mN",
            base_unit_symbol: "N",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Force",
            display_name: "Millinewton",
        },
    ],
    // Energy units
    [
        "J",
        {
            symbol: "J",
            base_unit_symbol: "J",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Energy",
            display_name: "Joule",
        },
    ],
    [
        "kJ",
        {
            symbol: "kJ",
            base_unit_symbol: "J",
            conversion_factor: 1000,
            offset: 0,
            dimension_name: "Energy",
            display_name: "Kilojoule",
        },
    ],
    [
        "mJ",
        {
            symbol: "mJ",
            base_unit_symbol: "J",
            conversion_factor: 0.001,
            offset: 0,
            dimension_name: "Energy",
            display_name: "Millijoule",
        },
    ],
    [
        "kcal",
        {
            symbol: "kcal",
            base_unit_symbol: "J",
            conversion_factor: 4184,
            offset: 0,
            dimension_name: "Energy",
            display_name: "Kilocalorie",
        },
    ],
    // Enzymatic activity units
    [
        "U/mL",
        {
            symbol: "U/mL",
            base_unit_symbol: "U/mL",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Enzymatic Activity",
            display_name: "Units per milliliter",
        },
    ],
    // Microbial count units
    [
        "CFU/g",
        {
            symbol: "CFU/g",
            base_unit_symbol: "CFU/g",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Microbial Count",
            display_name: "Colony forming units per gram",
        },
    ],
    [
        "cfu/g",
        {
            symbol: "cfu/g",
            base_unit_symbol: "CFU/g",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Microbial Count",
            display_symbol: "CFU/g",
        },
    ],
    [
        "CFU/ml",
        {
            symbol: "CFU/ml",
            base_unit_symbol: "CFU/ml",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Microbial Count (liquid)",
            display_name: "Colony forming units per millilitre",
        },
    ],
    [
        "CFU/mL",
        {
            symbol: "CFU/mL",
            base_unit_symbol: "CFU/ml",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Microbial Count (liquid)",
            display_symbol: "CFU/ml",
        },
    ],
    [
        "cfu/ml",
        {
            symbol: "cfu/ml",
            base_unit_symbol: "CFU/ml",
            conversion_factor: 1,
            offset: 0,
            dimension_name: "Microbial Count (liquid)",
            display_symbol: "CFU/ml",
        },
    ],
]);
export function getUnit(symbol) {
    return UNITS.get(symbol);
}
/**
 * Whether two unit strings name the same unit.
 *
 * Identical strings always do. Otherwise both must be registered and resolve
 * to the same base with the same factor — "%" and "g/100 g", "cfu/g" and
 * "CFU/g", "ug/kg" and "µg/kg". Units that merely share a dimension ("mg/kg"
 * and "g/kg") are convertible, not equivalent, and this returns false: a
 * registry unit set lists distinct bases, and a lab value in g/kg needs a
 * factor to land in a set that accepts mg/kg.
 */
export function unitsEquivalent(a, b) {
    const na = a.trim();
    const nb = b.trim();
    if (na === nb)
        return true;
    const ua = UNITS.get(na);
    const ub = UNITS.get(nb);
    if (!ua || !ub)
        return false;
    return (ua.base_unit_symbol === ub.base_unit_symbol &&
        ua.conversion_factor === ub.conversion_factor &&
        ua.offset === ub.offset);
}
/**
 * The member of `units` that `unit` is equivalent to, or null.
 *
 * An empty or null unit is the scalar unit "1": it matches an empty set, or a
 * set that lists "1" (a ratio, a pH, a water activity).
 */
export function unitInSet(unit, units) {
    const wanted = (unit ?? "").trim() || SCALAR_UNIT;
    if (wanted === SCALAR_UNIT && units.length === 0)
        return SCALAR_UNIT;
    for (const member of units) {
        if (unitsEquivalent(wanted, member))
            return member;
    }
    return null;
}
export function getBaseUnit(symbol) {
    const unit = getUnit(symbol);
    return unit?.base_unit_symbol ?? symbol;
}
export function getDisplaySymbol(symbol) {
    const unit = getUnit(symbol);
    return unit?.display_symbol ?? symbol;
}
export function convertToBaseUnit(value, unitSymbol) {
    const unitInfo = UNITS.get(unitSymbol);
    if (!unitInfo) {
        return value;
    }
    return value * unitInfo.conversion_factor + unitInfo.offset;
}
export function convertUnit(value, unitSymbol, targetUnitSymbol) {
    const baseValue = convertToBaseUnit(value, unitSymbol);
    const targetUnitInfo = UNITS.get(targetUnitSymbol);
    if (!targetUnitInfo) {
        return value;
    }
    return (baseValue - targetUnitInfo.offset) / targetUnitInfo.conversion_factor;
}
export function listCompatibleUnits(unitSymbol) {
    const baseUnit = getBaseUnit(unitSymbol);
    return Array.from(UNITS.entries())
        .filter(([_, unit]) => unit.base_unit_symbol === baseUnit)
        .map(([symbol]) => symbol);
}
/**
 * Get all unique dimension names from the unit system
 */
export function getDimensions() {
    const dimensions = new Set();
    for (const unit of UNITS.values()) {
        dimensions.add(unit.dimension_name);
    }
    return Array.from(dimensions).sort();
}
/**
 * Get all units belonging to a specific dimension
 */
export function getUnitsForDimension(dimensionName) {
    return Array.from(UNITS.values()).filter((unit) => unit.dimension_name === dimensionName);
}
/**
 * Get the base unit symbols for a dimension (for filtering)
 */
export function getBaseUnitsForDimension(dimensionName) {
    const baseUnits = new Set();
    for (const unit of UNITS.values()) {
        if (unit.dimension_name === dimensionName) {
            baseUnits.add(unit.base_unit_symbol);
        }
    }
    return Array.from(baseUnits);
}
/**
 * Get dimension name from a unit symbol
 */
export function getDimensionForUnit(unitSymbol) {
    const unit = getUnit(unitSymbol);
    return unit?.dimension_name;
}
//# sourceMappingURL=unit-system.js.map